import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNERSHIP_TYPES = [
  { id: "school", label: "School / University" },
  { id: "employer", label: "Employer" },
  { id: "nonprofit", label: "Nonprofit" },
  { id: "other", label: "Other" },
];

const BENEFITS = [
  "Branded app access for your community",
  "Dedicated 24/7 crisis response for your members",
  "pVerify-powered insurance navigation on your behalf",
  "Licensed provider network, sliding-scale pricing",
  "DocuSign consent flows (minors, emancipated youth)",
  "Quarterly impact & utilization reports",
];

export default function Partner() {
  const [form, setForm] = useState({
    organization: "",
    contact_name: "",
    email: "",
    role: "",
    phone: "",
    partnership_type: "school",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partner`, form);
      setDone(true);
      toast.success("Thank you — we'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-page" className="bg-white">
      <section className="py-20 lg:py-28 bg-gradient-hero" data-testid="partner-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">For organizations</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.05]">
            Bring 24/7 mental health to your <span className="hl-teal">entire community.</span>
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            Schools, employers, and nonprofits partner with Generational Health to give their people a real lifeline —
            not another benefits booklet.
          </p>
        </div>
      </section>

      <section className="py-20 bg-cream" data-testid="partner-benefits-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-slate-200" data-testid={`benefit-${i}`}>
                <span className="mt-1 w-6 h-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-navy font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="partner-form-section">
        <div className="max-w-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <Building2 className="w-10 h-10 text-teal mx-auto mb-4" />
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05] mb-4">
              Let's start the <span className="hl-teal">conversation.</span>
            </h2>
            <p className="text-lg text-muted-ink">
              Tell us about your organization and we'll reach out within two business days.
            </p>
          </div>

          {done ? (
            <div className="gh-card !p-10 text-center" data-testid="partner-success">
              <div className="w-16 h-16 rounded-full bg-teal flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-display text-3xl text-navy mb-3">Message received.</h3>
              <p className="text-muted-ink">A member of our partnerships team will be in touch very soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="gh-card !p-8 lg:!p-10 space-y-5" data-testid="partner-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow mb-2 block">Organization *</label>
                  <input required value={form.organization} onChange={update("organization")} className="gh-input" data-testid="partner-organization" />
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Your name *</label>
                  <input required value={form.contact_name} onChange={update("contact_name")} className="gh-input" data-testid="partner-contact-name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow mb-2 block">Email *</label>
                  <input required type="email" value={form.email} onChange={update("email")} className="gh-input" data-testid="partner-email" />
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Role</label>
                  <input value={form.role} onChange={update("role")} className="gh-input" data-testid="partner-role" />
                </div>
              </div>
              <div>
                <label className="eyebrow mb-2 block">Phone</label>
                <input value={form.phone} onChange={update("phone")} className="gh-input" data-testid="partner-phone" />
              </div>
              <div>
                <label className="eyebrow mb-2 block">Partnership type</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PARTNERSHIP_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, partnership_type: t.id }))}
                      className={`py-3 rounded-full text-sm font-bold border-2 transition ${
                        form.partnership_type === t.id
                          ? "border-teal bg-teal text-white"
                          : "border-slate-200 text-navy hover:border-teal/40"
                      }`}
                      data-testid={`partner-type-${t.id}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="eyebrow mb-2 block">Tell us about your needs</label>
                <textarea rows={4} value={form.message} onChange={update("message")} className="gh-input resize-none" data-testid="partner-message" />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="partner-submit">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : "Request a conversation"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
